import { FunctionComponent, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { Tabs } from '@idaho-aeyc/ui-kit';
import SubHeader from './SubHeader';

export interface IBreadcrumbItem {
  label: string;
  link?: string;
  onClick?: () => void;
}

export interface IHeaderTab {
  key: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface IPageHeaderProps {
  subtitle?: string;
  subtitleIcon?: ReactNode;
  tabs?: IHeaderTab[];
  activeTabKey?: string;
  onTabChange?: (key: string) => void;
  className?: string;
}

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  margin-bottom: 1px;
`;

const StyledTabs = styled(Tabs)`
  margin-top: 8px;

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-content-holder {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .ant-tabs-content {
    margin-bottom: 0;
    padding-bottom: 0;
    min-height: 0;
  }
`;

/**
 * PageHeader component with subtitle and tabs
 */
const PageHeader: FunctionComponent<IPageHeaderProps> = ({
  subtitle,
  subtitleIcon,
  tabs,
  activeTabKey,
  onTabChange,
  className,
}) => {
  const tabItems = useMemo(
    () =>
      tabs?.map(tab => ({
        ...tab,
        children: null,
      })) || [],
    [tabs],
  );

  const handleTabChange = (key: string) => {
    if (onTabChange) {
      onTabChange(key);
    }

    const tab = tabs?.find(t => t.key === key);
    if (tab?.onClick) {
      tab.onClick();
    }
  };

  return (
    <HeaderContainer className={className}>
      {subtitle && (
        <SubHeader title={subtitle} icon={subtitleIcon} iconSize={16} />
      )}

      {tabs && tabs.length > 0 && (
        <StyledTabs
          activeKey={activeTabKey}
          items={tabItems}
          onChange={handleTabChange}
          destroyInactiveTabPane={false}
        />
      )}
    </HeaderContainer>
  );
};

export default PageHeader;
